import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Users, Loader2, Trash2, Ban, CheckCircle, ArrowLeft
} from "lucide-react";

export function UserDetailView({ user, loading, onBack, onBlock, onDelete, onChangeRole, onChangeSubscription, formatDate }) {
  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-green-700" /></div>;
  if (!user) return null;

  const sub = user.subscription || {};
  const isBlocked = !!user.blocked;
  const isSA = user.role === "superadmin";

  return (
    <div className="space-y-4" data-testid="admin-user-detail">
      <Button variant="ghost" size="sm" onClick={onBack} className="gap-1 text-slate-500 hover:text-slate-700 rounded-sm">
        <ArrowLeft className="h-4 w-4" /> Torna alla lista
      </Button>

      <Card className="bg-white">
        <CardContent className="pt-6">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              {user.picture ? (
                <img src={user.picture} alt="" className="h-14 w-14 rounded-full object-cover border-2 border-slate-200" />
              ) : (
                <div className="h-14 w-14 rounded-full bg-purple-100 flex items-center justify-center">
                  <Users className="h-6 w-6 text-purple-700" />
                </div>
              )}
              <div>
                <h2 className="font-heading text-xl font-bold uppercase">{user.name || "-"}</h2>
                <p className="text-sm text-slate-500">{user.email}</p>
                <div className="flex gap-2 mt-2">
                  <Badge className={`rounded-sm text-xs ${isSA ? "bg-purple-100 text-purple-800 hover:bg-purple-100" : "bg-slate-100 text-slate-700 hover:bg-slate-100"}`}>
                    {isSA ? "Super Admin" : "Utente"}
                  </Badge>
                  {isBlocked && <Badge className="rounded-sm text-xs bg-red-100 text-red-800 hover:bg-red-100">Bloccato</Badge>}
                  <Badge className={`rounded-sm text-xs ${
                    sub.status === "active" ? "bg-green-100 text-green-800 hover:bg-green-100" :
                    sub.status === "trial" ? "bg-amber-100 text-amber-800 hover:bg-amber-100" :
                    "bg-red-100 text-red-800 hover:bg-red-100"
                  }`}>
                    {sub.status === "active" ? "Abbonamento Attivo" : sub.status === "trial" ? "Periodo di Prova" : "Scaduto"}
                  </Badge>
                </div>
              </div>
            </div>
            {!isSA && (
              <div className="flex gap-2">
                <Button data-testid="detail-block-btn" variant="outline" size="sm" className={`rounded-sm gap-1 text-xs ${isBlocked ? "text-green-700 border-green-200" : "text-amber-700 border-amber-200"}`} onClick={() => onBlock(user.user_id, !isBlocked)}>
                  {isBlocked ? <><CheckCircle className="h-3.5 w-3.5" /> Sblocca</> : <><Ban className="h-3.5 w-3.5" /> Blocca</>}
                </Button>
                <Button data-testid="detail-delete-btn" variant="outline" size="sm" className="rounded-sm gap-1 text-xs text-red-600 border-red-200" onClick={() => onDelete(user.user_id)}>
                  <Trash2 className="h-3.5 w-3.5" /> Elimina
                </Button>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 pt-4 border-t border-slate-100">
            <div>
              <p className="text-xs font-bold uppercase tracking-wide text-slate-400">Registrato il</p>
              <p className="text-sm font-medium mt-0.5">{formatDate(user.created_at)}</p>
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-wide text-slate-400">Ultimo Accesso</p>
              <p className="text-sm font-medium mt-0.5">{formatDate(user.last_login)}</p>
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-wide text-slate-400">Scadenza Abbonamento</p>
              <p className="text-sm font-medium mt-0.5">{formatDate(sub.subscription_end || sub.trial_end)}</p>
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-wide text-slate-400">Tornei Creati</p>
              <p className="text-sm font-medium mt-0.5">{user.tournaments?.length || 0}</p>
            </div>
          </div>

          {!isSA && (
            <div className="flex gap-4 mt-4 pt-4 border-t border-slate-100">
              <div>
                <Label className="text-xs font-bold uppercase tracking-wide text-slate-400">Cambia Ruolo</Label>
                <Select defaultValue={user.role} onValueChange={(v) => onChangeRole(user.user_id, v)}>
                  <SelectTrigger className="h-8 text-xs w-36 rounded-sm mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="user">Utente</SelectItem>
                    <SelectItem value="superadmin">Super Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wide text-slate-400">Cambia Abbonamento</Label>
                <Select defaultValue={sub.status || "none"} onValueChange={(v) => onChangeSubscription(user.user_id, v)}>
                  <SelectTrigger className="h-8 text-xs w-36 rounded-sm mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="trial">Prova</SelectItem>
                    <SelectItem value="active">Attivo</SelectItem>
                    <SelectItem value="expired">Scaduto</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {user.tournaments?.length > 0 && (
        <Card className="bg-white">
          <CardHeader><CardTitle className="font-heading text-base font-bold uppercase">Tornei dell'utente ({user.tournaments.length})</CardTitle></CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs uppercase">Nome</TableHead>
                  <TableHead className="text-xs uppercase">Date</TableHead>
                  <TableHead className="text-xs uppercase">Categorie</TableHead>
                  <TableHead className="text-xs uppercase">Giocatori</TableHead>
                  <TableHead className="text-xs uppercase">Match</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {user.tournaments.map(t => (
                  <TableRow key={t.tournament_id}>
                    <TableCell className="font-medium">{t.name}</TableCell>
                    <TableCell className="text-xs">{t.start_date} - {t.end_date}</TableCell>
                    <TableCell><Badge variant="outline" className="rounded-sm text-xs">{t.categories?.length || 0}</Badge></TableCell>
                    <TableCell><Badge variant="outline" className="rounded-sm text-xs">{t.total_players || 0}</Badge></TableCell>
                    <TableCell><Badge variant="outline" className="rounded-sm text-xs">{t.total_matches || 0}</Badge></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
